// friendlyTime — short, calm, human-readable date strings.
//
// Two helpers:
//   friendlyFutureTime(iso)  — for next_run_at, due_at, scheduled_for, etc.
//   friendlyPastTime(iso)    — for last_run_at, created_at, sent_at, etc.
//
// Both produce strings a coworker would actually say out loud:
//   "Tomorrow at 9:00 AM", "Friday at 3:00 PM", "May 30 at 9:00 AM",
//   "47 minutes ago", "Just now", "Overdue".
//
// The visible string is the story; the absolute timestamp lives in the
// `title` tooltip on the cell. No new dependencies — Intl handles locale.

function timeOnly(d) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function weekdayOnly(d) {
  return d.toLocaleDateString(undefined, { weekday: 'long' });
}
function monthDay(d) {
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}
function monthDayYear(d) {
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}
// Calendar-day delta: how many whole local days separate `then` from `now`,
// ignoring time-of-day. 0 = same day, 1 = next day, -1 = yesterday.
function dayDelta(now, then) {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.round((b - a) / 86400000);
}

export function friendlyFutureTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return '—';
  const now = new Date();
  const secs = (then - now) / 1000;
  if (secs <= 0) return 'Overdue';
  if (secs < 60) return 'Any moment';
  if (secs < 3600) {
    const m = Math.round(secs / 60);
    if (m <= 1) return 'Any moment';
    return `In ${m} minutes`;
  }
  const dd = dayDelta(now, then);
  if (dd === 0) return `Today at ${timeOnly(then)}`;
  if (dd === 1) return `Tomorrow at ${timeOnly(then)}`;
  if (dd > 1 && dd <= 6) return `${weekdayOnly(then)} at ${timeOnly(then)}`;
  if (then.getFullYear() === now.getFullYear()) return `${monthDay(then)} at ${timeOnly(then)}`;
  return monthDayYear(then);
}

export function friendlyPastTime(iso) {
  if (!iso) return 'Never run yet';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'Never run yet';
  const now = new Date();
  const secs = (now - then) / 1000;
  if (secs < 60) return 'Just now';
  if (secs < 3600) {
    const m = Math.round(secs / 60);
    if (m <= 1) return 'Just now';
    return `${m} minutes ago`;
  }
  const dd = dayDelta(now, then); // negative for past
  if (dd === 0) return `Today at ${timeOnly(then)}`;
  if (dd === -1) return `Yesterday at ${timeOnly(then)}`;
  if (dd < -1 && dd >= -6) return `${weekdayOnly(then)} at ${timeOnly(then)}`;
  if (then.getFullYear() === now.getFullYear()) return `${monthDay(then)} at ${timeOnly(then)}`;
  return monthDayYear(then);
}
